/**
 * New-version detection (§9.3): when a new service worker is waiting, the app shows
 * "A new version is available" [Reload]; reloading activates it and refreshes once.
 */
import { create } from 'zustand';

interface UpdateState {
  waiting: ServiceWorker | null;
  apply(): void;
}

export const useAppUpdate = create<UpdateState>((_set, get) => ({
  waiting: null,
  apply() {
    const worker = get().waiting;
    if (!worker) return;
    navigator.serviceWorker.addEventListener('controllerchange', () => location.reload(), { once: true });
    worker.postMessage({ type: 'SKIP_WAITING' });
  },
}));

/** Watch a registration for updates and surface a waiting worker. */
export function watchForUpdates(registration: ServiceWorkerRegistration): void {
  const offer = (worker: ServiceWorker | null) => {
    // Only offer an update when a previous version controls the page (not on first install).
    if (worker && navigator.serviceWorker.controller) useAppUpdate.setState({ waiting: worker });
  };
  offer(registration.waiting);
  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    installing?.addEventListener('statechange', () => {
      if (installing.state === 'installed') offer(installing);
    });
  });
  // Check again when the app returns to the foreground (PWAs can stay open for days).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void registration.update().catch(() => undefined);
  });
}
